import { useEffect, useRef, useState } from "react";
import api from "../api/client";

interface Notification {
  id: string;
  message: string;
  detail?: string | null;
  type: "success" | "warning" | "info" | "error";
  time: string;
  is_read: boolean;
}

const BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const iconFor = (type: string) => {
  if (type === "success") return "✅";
  if (type === "warning") return "⚠️";
  if (type === "error")   return "✗";
  return "ℹ";
};

const colorFor = (type: string) => {
  if (type === "success") return "text-green-600";
  if (type === "warning") return "text-amber-500";
  if (type === "error")   return "text-red-500";
  return "text-blue-500";
};

const dotFor = (type: string) => {
  if (type === "success") return "bg-green-500";
  if (type === "warning") return "bg-amber-500";
  if (type === "error")   return "bg-red-500";
  return "bg-blue-500";
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 1)  return "just now";
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export function NotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen]     = useState(false);
  const [loading, setLoading] = useState(true);
  const esRef = useRef<EventSource | null>(null);

  const unread = notifications.filter(n => !n.is_read).length;

  // Load persisted notifications on mount
  useEffect(() => {
    api.get("/api/notifications")
      .then(r => setNotifications(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Open SSE connection for real-time push
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const es = new EventSource(`${BASE_URL}/api/notifications/stream?token=${token}`);
    esRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Ignore the initial "connected" ack
        if (data.type === "connected") return;
        const notif = data as Notification;
        setNotifications(prev => [notif, ...prev].slice(0, 50));
      } catch {
        // ignore malformed messages
      }
    };

    es.onerror = () => {
      // EventSource auto-reconnects; nothing to do
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, []);

  const markAllRead = async () => {
    await api.post("/api/notifications/read-all").catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const clearAll = async () => {
    await api.delete("/api/notifications").catch(() => {});
    setNotifications([]);
    setOpen(false);
  };

  const markRead = async (id: string) => {
    await api.post(`/api/notifications/${id}/read`).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
  };

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen(o => !o); if (!open) markAllRead(); }}
        className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 relative"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-96 bg-white rounded-xl border border-gray-200 shadow-xl z-[9999]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-gray-900">Notifications</p>
              {unread > 0 && (
                <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded-full">
                  {unread}
                </span>
              )}
            </div>
            <button
              onClick={clearAll}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Clear all
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
            {loading ? (
              <div className="px-4 py-8 text-center text-gray-400 text-sm">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-400 text-sm">No notifications</div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => markRead(n.id)}
                  className={`px-4 py-3 cursor-pointer transition-colors ${n.is_read ? "hover:bg-gray-50" : "bg-blue-50/40 hover:bg-blue-50"}`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`text-base mt-0.5 ${colorFor(n.type)}`}>
                      {iconFor(n.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${n.is_read ? "text-gray-700 font-normal" : "text-gray-900 font-medium"}`}>
                        {n.message}
                      </p>
                      {n.detail && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{n.detail}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">{relativeTime(n.time)}</p>
                    </div>
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${n.is_read ? "bg-gray-200" : dotFor(n.type)}`} />
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 rounded-b-xl flex items-center justify-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <p className="text-xs text-gray-400">Live updates connected</p>
          </div>
        </div>
      )}
    </div>
  );
}
